import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import queryString from 'query-string';
import { useAppDispatch } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import { fetchConnectionsEffect } from 'app/store/Profile/effects';
import { config } from 'config';
import { ConnectionStatus } from 'app/utils/utils.types';
import { getConnectionStatusMessage } from 'app/utils/utils';
import {
  patchReleaseExportEffect,
  updateReleaseExportEffect,
} from 'app/store/Releases/effects';
import { Release } from 'types/releases';
import {
  connectedToSoundcloudAction,
  connectedToTuneCoreAction,
} from 'app/store/Mixpanel/slice';
import { AccountConnectionProviderId } from 'types/profile';
import { useReleaseExport } from 'app/views/Release/hooks';

export const useExportDraft = (releaseGuid: string | undefined) => {
  const dispatch = useAppDispatch();

  const completeReleaseExport = useCallback(
    async (
      data: { accountConnectionGuid: string },
      providerId: AccountConnectionProviderId,
      releaseExportGuid: string
    ) => {
      if (releaseGuid) {
        try {
          await dispatch(
            patchReleaseExportEffect({ data, releaseExportGuid, providerId })
          ).unwrap();
          await dispatch(
            updateReleaseExportEffect({
              releaseExportGuid,
              releaseGuid,
              providerId,
            })
          );
        } catch (e) {
          // proceed
        }
      } else {
        throw new Error(
          'releaseGuid is not defined in completeReleaseExport method'
        );
      }
    },
    [dispatch, releaseGuid]
  );

  return { completeReleaseExport };
};

export const useHandleProviderConnectionPayload = (
  release: Release | undefined
) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { completeReleaseExport } = useExportDraft(release?.guid);
  const { exportConnexRelease } = useReleaseExport();

  useEffect(() => {
    if (release) {
      const parsed = queryString.parse(location.search);
      const releaseExportGuid = parsed?.releaseExportGuid as string;
      const accountConnectionGuid = parsed?.connectionGuid as string;
      const connectionStatus = parsed?.connectionStatus as ConnectionStatus;
      const connectionName = parsed?.connectionName as string | undefined;
      const providerId = parsed?.providerId as AccountConnectionProviderId;
      const shouldRefetchConnections =
        connectionStatus === 'successful' &&
        config.isElectronBuild &&
        providerId;

      if (connectionStatus) {
        const message = getConnectionStatusMessage(
          connectionStatus,
          providerId,
          connectionName
        );
        if (shouldRefetchConnections) {
          dispatch(fetchConnectionsEffect({ providerId }));
        }

        const ignoreMessage =
          connectionStatus === 'successful' && providerId === 'Connex';
        if (message && !ignoreMessage) {
          dispatch(
            addAlertAction({
              message,
              autoHideDuration: 4000,
            })
          );
        }

        if (connectionStatus === 'successful') {
          if (accountConnectionGuid && releaseExportGuid) {
            completeReleaseExport(
              { accountConnectionGuid },
              providerId,
              releaseExportGuid
            );
          }
          if (providerId === 'SoundCloud') {
            dispatch(connectedToSoundcloudAction());
          }
          if (providerId === 'TuneCore') {
            dispatch(connectedToTuneCoreAction());
          }
          if (providerId === 'Connex') {
            exportConnexRelease(
              { shipmentStrategy: 'Connex' },
              parsed.connectionGuid as string
            );
          }
        }
        navigate({ search: '' }, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    completeReleaseExport,
    navigate,
    location.search,
    exportConnexRelease,
  ]);
};
